import { ReactNode, useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import { appEmitter } from '../core/EventEmitter';
import { useDelayedLoadingState } from '../hooks/useDelayedLoadingState';
import { useInterval } from '../hooks/useInterval';
import { useMaintenanceModeCheck } from '../hooks/useMaintenanceModeCheck';
import { MaintenancePagePlaceholder } from '../pages/Maintenance/MaintenancePage';
import { setMaintenanceMode } from '../redux/appState/appState';

const SPINNER_DELAY = 300;
const POLLING_INTERVAL = 5 * 60 * 1000;

/**
 * Uses the useHealthCheck hook to manage state and logic.
 * Conditionally renders MaintenancePage or children.
 * Shows a delayed loading spinner during the initial check or retries.
 */
export const HealthCheck = ({ children }: { children: ReactNode }) => {
  const { isMaintenanceMode, isChecking, retryCheck } = useMaintenanceModeCheck();
  const dispatch = useDispatch();
  const [initialCheckCompleted, setInitialCheckCompleted] = useState(isMaintenanceMode);
  const showSpinner = useDelayedLoadingState(isChecking && !initialCheckCompleted, SPINNER_DELAY);

  useInterval(() => {
    console.log('Interval: Triggering health check retry...');
    retryCheck();
  }, POLLING_INTERVAL);

  useEffect(() => {
    const handleMaintenanceEvent = () => {
      if (!isMaintenanceMode) {
        console.warn('HealthCheck component listener: Maintenance event received, dispatching ON.');
        dispatch(setMaintenanceMode(true));
      }
    };
    const unsubscribe = appEmitter.on('maintenanceModeDetected', handleMaintenanceEvent);

    return () => {
      unsubscribe();
    };
  }, [dispatch, isMaintenanceMode]);

  useEffect(() => {
    if (!isChecking && !initialCheckCompleted) {
      console.log('Initial health check marked as completed.');
      setInitialCheckCompleted(true);
    }
  }, [isChecking, initialCheckCompleted]);

  if (isMaintenanceMode) {
    return <MaintenancePagePlaceholder onRetry={retryCheck} isRetrying={isChecking} />;
  }

  if (!initialCheckCompleted) {
    if (showSpinner) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <div>Loading...</div>
        </div>
      );
    }

    return null;
  }

  if (initialCheckCompleted && !isMaintenanceMode) {
    return <>{children}</>;
  }

  return null;
};
